import { useState, useEffect, useCallback } from 'react';
import profileApi from '../services/profileApi';

export const useProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await profileApi.getProfile();
      setProfile(data.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }, []);

  // Update profile
  const updateProfile = useCallback(async (profileData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const updatedProfile = await profileApi.updateProfile(profileData);
      setProfile(updatedProfile);
      setSuccess('Profile updated successfully');
      return updatedProfile;
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Upload avatar
  const uploadAvatar = useCallback(async (file) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await profileApi.uploadAvatar(file);
      // Update profile with new avatar URL
      setProfile(prev => ({ ...prev, avatar: result.avatarUrl }));
      setSuccess('Avatar uploaded successfully');
      return result;
    } catch (err) {
      setError(err.message || 'Failed to upload avatar');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Remove avatar
  const removeAvatar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await profileApi.removeAvatar();
      setProfile(prev => ({ ...prev, avatar: null }));
      setSuccess('Avatar removed successfully');
    } catch (err) {
      setError(err.message || 'Failed to remove avatar');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear messages
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    success,
    fetchProfile,
    updateProfile,
    uploadAvatar,
    removeAvatar,
    clearMessages,
  };
};